/**
 * LiveSignLanguagePanel - Professional sign language interpreter panel
 * Features:
 * - Real-time Web Speech API for capturing audio with visualizer
 * - 26 international sign languages including 8 African regional languages
 * - Audio level visualization and confidence indicators
 * - Demo mode with sample phrases
 * - Manual text input for testing
 * - Customizable professional avatar appearance
 * - Multi-language speech recognition support
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Mic, MicOff, Settings, X, AlertCircle, Play, Pause, Type,
  Send, Globe, User, Palette, Languages, Shirt, Volume2, Activity,
  Radio, Waves, Zap
} from 'lucide-react';
import SignLanguageAvatarLive, { SIGN_LANGUAGE_CONFIG, SKIN_TONES, HAIR_COLORS } from './SignLanguageAvatarLive';

// Grouped sign languages for better organization - 26 languages total
const SIGN_LANGUAGE_GROUPS = {
  '🌍 Africa (8)': ['sasl', 'zsl', 'ksl', 'ugsl', 'tsl', 'gsl', 'nsl', 'esl'],
  '🌎 Americas (4)': ['asl', 'lsm', 'libras', 'lsa'],
  '🌍 Europe (7)': ['bsl', 'lsf', 'dgs', 'lis', 'lse', 'pjm', 'rsl'],
  '🌏 Asia-Pacific (6)': ['auslan', 'nzsl', 'jsl', 'csl', 'isl_india', 'hksl'],
  '🌐 International (1)': ['is'],
};

// Speech recognition language codes mapping
const SPEECH_LANG_CODES = {
  'en': 'en-US',
  'es': 'es-ES',
  'fr': 'fr-FR',
  'de': 'de-DE',
  'pt': 'pt-BR',
  'ja': 'ja-JP',
  'zh': 'zh-CN',
  'ko': 'ko-KR',
  'ru': 'ru-RU',
  'it': 'it-IT',
  'pl': 'pl-PL',
  'ar': 'ar-SA',
  'hi': 'hi-IN',
  'sw': 'sw-KE', // Swahili for African regions
  'zu': 'zu-ZA', // Zulu for South Africa
};

// Demo phrases for testing
const DEMO_PHRASES = [
  "Welcome to the conference",
  "Thank you for joining us today",
  "Please take your seats",
  "The session will begin shortly",
  "Are there any questions?",
  "Let's take a short break",
  "We will resume in 10 minutes",
  "Thank you for your attention",
  "Please remember to complete the survey",
  "Safe travels everyone",
  "See you at the next session!",
  "Don't forget to network during lunch",
];

// Clothing color presets
const CLOTHING_PRESETS = [
  { name: 'Professional Blue', color: '#3B82F6' },
  { name: 'Corporate Navy', color: '#1E3A5F' },
  { name: 'Emerald Green', color: '#10B981' },
  { name: 'Royal Purple', color: '#7C3AED' },
  { name: 'Classic Black', color: '#1F2937' },
  { name: 'Burgundy', color: '#7F1D1D' },
  { name: 'Teal', color: '#0D9488' },
  { name: 'Coral', color: '#F97316' },
];

// Audio Visualizer Component
const AudioVisualizer = ({ isActive, audioLevel }) => {
  const bars = 12;
  
  return (
    <div className="flex items-end justify-center gap-0.5 h-8 px-2">
      {[...Array(bars)].map((_, i) => {
        const baseHeight = Math.sin((i / bars) * Math.PI) * 0.7 + 0.3;
        const animatedHeight = isActive 
          ? baseHeight * (0.3 + audioLevel * 0.7 * (0.5 + Math.random() * 0.5))
          : 0.15;
        
        return (
          <div
            key={i}
            className={`w-1 rounded-full transition-all duration-75 ${
              isActive ? 'bg-gradient-to-t from-emerald-500 to-emerald-300' : 'bg-slate-400'
            }`}
            style={{
              height: `${animatedHeight * 100}%`,
              opacity: isActive ? 0.8 + audioLevel * 0.2 : 0.4,
            }}
          />
        );
      })}
    </div>
  );
};

// Confidence Indicator Component
const ConfidenceIndicator = ({ confidence, isActive }) => {
  if (!isActive) return null;
  
  const getColor = () => {
    if (confidence >= 0.8) return 'bg-emerald-500';
    if (confidence >= 0.5) return 'bg-amber-500';
    return 'bg-red-500';
  };
  
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <div className={`w-2 h-2 rounded-full ${getColor()} animate-pulse`} />
      <span className="text-slate-500">
        {Math.round(confidence * 100)}% confident
      </span>
    </div>
  );
};

const LiveSignLanguagePanel = ({
  position = 'bottom-right',
  size = 'medium',
  language = 'asl',
  skinTone = 'medium',
  onClose,
  onSettingsChange,
  defaultExpanded = false,
  showSettings = true,
  externalText = null,
}) => {
  // State
  const [isListening, setIsListening] = useState(false);
  const [currentText, setCurrentText] = useState('');
  const [transcript, setTranscript] = useState([]);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(true);
  const [error, setError] = useState(null);

  // Demo & Manual input state
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoIndex, setDemoIndex] = useState(0);
  const [manualText, setManualText] = useState('');
  const [inputMode, setInputMode] = useState('mic');
  
  // Audio visualization state
  const [audioLevel, setAudioLevel] = useState(0);
  const [confidence, setConfidence] = useState(0);
  const [speechLang, setSpeechLang] = useState('en-US');
  const [wordsPerMinute, setWordsPerMinute] = useState(0);
  const [totalWords, setTotalWords] = useState(0);
  const [sessionDuration, setSessionDuration] = useState(0);

  // Settings state - enhanced with new options
  const [settings, setSettings] = useState({
    language: language,
    skinTone: skinTone,
    hairColor: 'dark-brown',
    clothingColor: '#3B82F6',
    showCaption: true,
    autoStart: false,
    showVisualizer: true,
    showTranscriptHistory: false,
    speechLanguage: 'en',
  });

  // Refs
  const recognitionRef = useRef(null);
  const restartTimeoutRef = useRef(null);
  const demoIntervalRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const audioAnimationRef = useRef(null);
  const sessionStartRef = useRef(null);
  const wordCountRef = useRef(0);

  // Check for speech recognition support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSpeechSupported(false);
    }
  }, []);

  // Initialize audio analyser for visualization
  const initAudioAnalyser = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      
      const updateAudioLevel = () => {
        if (!analyserRef.current || !isListening) {
          setAudioLevel(0);
          return;
        }
        
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        const normalizedLevel = Math.min(1, average / 128);
        setAudioLevel(normalizedLevel);
        
        audioAnimationRef.current = requestAnimationFrame(updateAudioLevel);
      };
      
      updateAudioLevel();
      return stream;
    } catch (err) {
      console.error('Failed to initialize audio analyser:', err);
      return null;
    }
  }, [isListening]);

  // Session duration timer
  useEffect(() => {
    let interval;
    if (isListening && inputMode === 'mic') {
      if (!sessionStartRef.current) {
        sessionStartRef.current = Date.now();
      }
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - sessionStartRef.current) / 1000);
        setSessionDuration(elapsed);
        
        // Calculate WPM
        if (elapsed > 0 && wordCountRef.current > 0) {
          setWordsPerMinute(Math.round((wordCountRef.current / elapsed) * 60));
        }
      }, 1000);
    } else {
      sessionStartRef.current = null;
      setSessionDuration(0);
      wordCountRef.current = 0;
    }
    return () => clearInterval(interval);
  }, [isListening, inputMode]);

  // Initialize speech recognition with enhanced features
  const initSpeechRecognition = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return null;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 3;
    recognition.lang = SPEECH_LANG_CODES[settings.speechLanguage] || 'en-US';

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';
      let maxConfidence = 0;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        const conf = result[0].confidence || 0.8;
        
        maxConfidence = Math.max(maxConfidence, conf);
        
        if (result.isFinal) {
          finalTranscript += transcript + ' ';
          // Count words for WPM
          wordCountRef.current += transcript.trim().split(/\s+/).length;
          setTotalWords(wordCountRef.current);
        } else {
          interimTranscript += transcript;
        }
      }

      setConfidence(maxConfidence);
      
      const text = finalTranscript || interimTranscript;
      if (text.trim()) {
        setCurrentText(text.trim());

        if (finalTranscript.trim()) {
          setTranscript(prev => [...prev.slice(-50), {
            text: finalTranscript.trim(),
            timestamp: Date.now(),
            confidence: maxConfidence
          }]);
        }
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);

      if (event.error === 'not-allowed') {
        setError('Microphone access denied. Please allow microphone access in your browser settings.');
        setIsListening(false);
        // Stop audio analyser inline to avoid circular dependency
        if (audioAnimationRef.current) {
          cancelAnimationFrame(audioAnimationRef.current);
          audioAnimationRef.current = null;
        }
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
          audioContextRef.current = null;
        }
        setAudioLevel(0);
      } else if (event.error === 'no-speech') {
        // Auto-restart on silence
        if (isListening && inputMode === 'mic') {
          restartTimeoutRef.current = setTimeout(() => {
            if (recognitionRef.current && isListening) {
              try {
                recognitionRef.current.start();
              } catch (e) { }
            }
          }, 100);
        }
      } else if (event.error === 'network') {
        setError('Network error. Please check your internet connection.');
      } else if (event.error !== 'aborted') {
        setError(`Speech recognition error: ${event.error}`);
      }
    };

    recognition.onend = () => {
      // Auto-restart for continuous listening
      if (isListening && inputMode === 'mic') {
        restartTimeoutRef.current = setTimeout(() => {
          if (recognitionRef.current && isListening) {
            try {
              recognitionRef.current.start();
            } catch (e) { }
          }
        }, 100);
      }
    };

    recognition.onspeechstart = () => {
      // Speech detected
      setError(null);
    };

    return recognition;
  }, [isListening, inputMode, settings.speechLanguage]);

  // Stop audio analyser
  const stopAudioAnalyser = useCallback(() => {
    if (audioAnimationRef.current) {
      cancelAnimationFrame(audioAnimationRef.current);
      audioAnimationRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    setAudioLevel(0);
  }, []);

  // Start/stop microphone listening with audio visualization
  const toggleMicListening = useCallback(async () => {
    if (!speechSupported) {
      toast.error('Speech recognition not supported. Try Chrome or Edge.');
      return;
    }

    if (isListening && inputMode === 'mic') {
      // Stop listening
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
      stopAudioAnalyser();
      setIsListening(false);
      setCurrentText('');
      setConfidence(0);
      setInputMode('mic');
      toast.info('Stopped listening');
    } else {
      // Start listening
      stopDemo();
      setError(null);
      setInputMode('mic');

      // Initialize audio analyser for visualization
      if (settings.showVisualizer) {
        await initAudioAnalyser();
      }

      if (!recognitionRef.current) {
        recognitionRef.current = initSpeechRecognition();
      }

      if (recognitionRef.current) {
        try {
          // Update language if changed
          recognitionRef.current.lang = SPEECH_LANG_CODES[settings.speechLanguage] || 'en-US';
          recognitionRef.current.start();
          setIsListening(true);
          toast.success('Listening to microphone...');
        } catch (e) {
          if (e.message !== 'recognition has already started') {
            setError('Failed to start microphone');
          }
        }
      }
    }
  }, [isListening, speechSupported, initSpeechRecognition, inputMode, stopDemo, stopAudioAnalyser, initAudioAnalyser, settings.showVisualizer, settings.speechLanguage]);

  // Demo mode functions
  const startDemo = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    stopAudioAnalyser();
    setIsListening(false);

    setInputMode('demo');
    setIsDemoMode(true);
    setDemoIndex(0);
    setCurrentText(DEMO_PHRASES[0]);

    demoIntervalRef.current = setInterval(() => {
      setDemoIndex(prev => {
        const next = (prev + 1) % DEMO_PHRASES.length;
        setCurrentText(DEMO_PHRASES[next]);
        return next;
      });
    }, 3500);

    toast.success('Demo mode started');
  }, [stopAudioAnalyser]);

  const stopDemo = useCallback(() => {
    if (demoIntervalRef.current) {
      clearInterval(demoIntervalRef.current);
      demoIntervalRef.current = null;
    }
    setIsDemoMode(false);
    if (inputMode === 'demo') {
      setCurrentText('');
      setInputMode('mic');
    }
  }, [inputMode]);

  const toggleDemo = useCallback(() => {
    if (isDemoMode) {
      stopDemo();
    } else {
      startDemo();
    }
  }, [isDemoMode, startDemo, stopDemo]);

  // Manual text input
  const sendManualText = useCallback(() => {
    if (!manualText.trim()) return;

    stopDemo();
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);

    setInputMode('manual');
    setCurrentText(manualText.trim());

    setTranscript(prev => [...prev.slice(-50), {
      text: manualText.trim(),
      timestamp: Date.now(),
      manual: true
    }]);

    setManualText('');

    setTimeout(() => {
      if (inputMode === 'manual') {
        setCurrentText('');
      }
    }, manualText.split(' ').length * 600 + 1000);
  }, [manualText, stopDemo, inputMode]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
      if (demoIntervalRef.current) {
        clearInterval(demoIntervalRef.current);
      }
      if (audioAnimationRef.current) {
        cancelAnimationFrame(audioAnimationRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  // Handle external text
  useEffect(() => {
    if (externalText) {
      setCurrentText(externalText);
    }
  }, [externalText]);

  // Update settings
  const updateSetting = (key, value) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      if (onSettingsChange) {
        onSettingsChange(newSettings);
      }
      return newSettings;
    });
  };

  // Format session duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Position styles
  const positionStyles = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
  };

  // Status helpers
  const getStatusText = () => {
    if (isDemoMode) return 'DEMO';
    if (isListening) return 'LIVE';
    if (inputMode === 'manual' && currentText) return 'MANUAL';
    return 'READY';
  };

  const getStatusColor = () => {
    if (isDemoMode) return 'bg-purple-500';
    if (isListening) return 'bg-emerald-500';
    if (inputMode === 'manual' && currentText) return 'bg-blue-500';
    return 'bg-slate-500';
  };

  const langConfig = SIGN_LANGUAGE_CONFIG[settings.language] || SIGN_LANGUAGE_CONFIG.asl;

  return (
    <div
      className={`fixed ${positionStyles[position]} z-50 transition-all duration-300`}
      data-testid="live-sign-language-panel"
    >
      <div className="relative">
        {/* Settings Panel - positioned to stay within viewport */}
        {showSettingsPanel && showSettings && (
          <div
            className="fixed inset-0 z-[100]"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowSettingsPanel(false);
              }
            }}
          >
            <Card 
              className="absolute shadow-2xl animate-in slide-in-from-right-2 border-slate-200 bg-white"
              style={{
                width: '340px',
                maxHeight: '85vh',
                overflowY: 'auto',
                right: '220px',
                bottom: '16px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <Languages className="h-4 w-4 text-blue-600" />
                  Sign Language Interpreter
                </h4>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setShowSettingsPanel(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <Tabs defaultValue="input" className="w-full">
                <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 h-9">
                  <TabsTrigger value="input" className="text-xs">
                    <Mic className="h-3 w-3 mr-1" />
                    Input
                  </TabsTrigger>
                  <TabsTrigger value="avatar" className="text-xs">
                    <User className="h-3 w-3 mr-1" />
                    Avatar
                  </TabsTrigger>
                  <TabsTrigger value="language" className="text-xs">
                    <Globe className="h-3 w-3 mr-1" />
                    Language
                  </TabsTrigger>
                </TabsList>

                {/* Input Tab */}
                <TabsContent value="input" className="space-y-3 mt-3">
                  {/* Live Audio Stats when listening */}
                  {isListening && inputMode === 'mic' && (
                    <div className="p-3 bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg border border-emerald-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Radio className="h-4 w-4 text-emerald-600 animate-pulse" />
                          <span className="text-sm font-semibold text-emerald-700">Live Interpretation</span>
                        </div>
                        <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 text-[10px]">
                          {formatDuration(sessionDuration)}
                        </Badge>
                      </div>
                      
                      {/* Audio Visualizer */}
                      {settings.showVisualizer && (
                        <div className="bg-white/80 rounded-lg p-2 mb-2">
                          <AudioVisualizer isActive={isListening} audioLevel={audioLevel} />
                        </div>
                      )}
                      
                      {/* Stats Row */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-center">
                        <div className="bg-white/60 rounded p-1.5">
                          <p className="text-lg font-bold text-emerald-600">{totalWords}</p>
                          <p className="text-[9px] text-slate-500">Words</p>
                        </div>
                        <div className="bg-white/60 rounded p-1.5">
                          <p className="text-lg font-bold text-blue-600">{wordsPerMinute}</p>
                          <p className="text-[9px] text-slate-500">WPM</p>
                        </div>
                        <div className="bg-white/60 rounded p-1.5">
                          <p className="text-lg font-bold text-purple-600">{Math.round(confidence * 100)}%</p>
                          <p className="text-[9px] text-slate-500">Accuracy</p>
                        </div>
                      </div>
                      
                      <ConfidenceIndicator confidence={confidence} isActive={isListening} />
                    </div>
                  )}

                  {/* Microphone Control */}
                  <div className={`p-3 rounded-lg border transition-all ${isListening && inputMode === 'mic' ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-full transition-colors ${isListening && inputMode === 'mic' ? 'bg-emerald-200' : 'bg-emerald-100'}`}>
                          {isListening && inputMode === 'mic' ? (
                            <Waves className="h-3.5 w-3.5 text-emerald-600 animate-pulse" />
                          ) : (
                            <Mic className="h-3.5 w-3.5 text-emerald-600" />
                          )}
                        </div>
                        <Label className="text-xs font-medium">
                          {isListening && inputMode === 'mic' ? 'Listening...' : 'Microphone'}
                        </Label>
                      </div>
                      <Button
                        size="sm"
                        variant={isListening && inputMode === 'mic' ? 'destructive' : 'default'}
                        className={`h-7 text-xs transition-all ${isListening && inputMode === 'mic' ? '' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                        onClick={toggleMicListening}
                        disabled={!speechSupported}
                      >
                        {isListening && inputMode === 'mic' ? (
                          <><MicOff className="h-3 w-3 mr-1" /> Stop</>
                        ) : (
                          <><Zap className="h-3 w-3 mr-1" /> Start Live</>
                        )}
                      </Button>
                    </div>
                    <p className="text-[10px] text-slate-500">
                      {speechSupported
                        ? 'Real-time speech to sign language interpretation'
                        : 'Not supported in this browser. Try Chrome or Edge.'}
                    </p>
                  </div>

                  {/* Speech Language Selector */}
                  <div className="p-3 bg-slate-50 rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-indigo-100 rounded-full">
                        <Volume2 className="h-3.5 w-3.5 text-indigo-600" />
                      </div>
                      <Label className="text-xs font-medium">Speech Language</Label>
                    </div>
                    <Select
                      value={settings.speechLanguage}
                      onValueChange={(v) => updateSetting('speechLanguage', v)}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">🇺🇸 English (US)</SelectItem>
                        <SelectItem value="es">🇪🇸 Spanish</SelectItem>
                        <SelectItem value="fr">🇫🇷 French</SelectItem>
                        <SelectItem value="de">🇩🇪 German</SelectItem>
                        <SelectItem value="pt">🇧🇷 Portuguese</SelectItem>
                        <SelectItem value="ja">🇯🇵 Japanese</SelectItem>
                        <SelectItem value="zh">🇨🇳 Chinese</SelectItem>
                        <SelectItem value="ko">🇰🇷 Korean</SelectItem>
                        <SelectItem value="ru">🇷🇺 Russian</SelectItem>
                        <SelectItem value="it">🇮🇹 Italian</SelectItem>
                        <SelectItem value="sw">🇰🇪 Swahili</SelectItem>
                        <SelectItem value="zu">🇿🇦 Zulu</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[9px] text-slate-400 mt-1">
                      Language for speech recognition input
                    </p>
                  </div>

                  {/* Demo Mode */}
                  <div className={`p-3 rounded-lg border transition-all ${isDemoMode ? 'bg-purple-50 border-purple-300' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-full ${isDemoMode ? 'bg-purple-200' : 'bg-purple-100'}`}>
                          <Play className="h-3.5 w-3.5 text-purple-600" />
                        </div>
                        <Label className="text-xs font-medium">Demo Mode</Label>
                      </div>
                      <Button
                        size="sm"
                        variant={isDemoMode ? 'destructive' : 'secondary'}
                        className="h-7 text-xs"
                        onClick={toggleDemo}
                      >
                        {isDemoMode ? (
                          <><Pause className="h-3 w-3 mr-1" /> Stop</>
                        ) : (
                          <><Play className="h-3 w-3 mr-1" /> Start Demo</>
                        )}
                      </Button>
                    </div>
                    <p className="text-[10px] text-slate-500">
                      Preview with sample conference phrases
                    </p>
                  </div>

                  {/* Manual Text */}
                  <div className="p-3 bg-slate-50 rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-blue-100 rounded-full">
                        <Type className="h-3.5 w-3.5 text-blue-600" />
                      </div>
                      <Label className="text-xs font-medium">Manual Input</Label>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={manualText}
                        onChange={(e) => setManualText(e.target.value)}
                        placeholder="Type text to interpret..."
                        className="h-8 text-xs"
                        onKeyDown={(e) => e.key === 'Enter' && sendManualText()}
                      />
                      <Button
                        size="sm"
                        className="h-8 px-3"
                        onClick={sendManualText}
                        disabled={!manualText.trim()}
                      >
                        <Send className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Visualizer Toggle */}
                  <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Activity className="h-3.5 w-3.5 text-slate-500" />
                      <Label className="text-xs">Show Audio Visualizer</Label>
                    </div>
                    <Switch
                      checked={settings.showVisualizer}
                      onCheckedChange={(v) => updateSetting('showVisualizer', v)}
                    />
                  </div>
                </TabsContent>

                {/* Avatar Tab */}
                <TabsContent value="avatar" className="space-y-4 mt-3">
                  {/* Skin Tone */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium flex items-center gap-2">
                      <User className="h-3.5 w-3.5" />
                      Skin Tone
                    </Label>
                    <div className="flex gap-1.5 flex-wrap">
                      {Object.entries(SKIN_TONES).map(([id, tone]) => (
                        <button
                          key={id}
                          className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 ${settings.skinTone === id
                              ? 'border-blue-500 scale-110 shadow-lg'
                              : 'border-transparent hover:border-slate-300'
                            }`}
                          style={{ background: `linear-gradient(135deg, ${tone.highlight} 0%, ${tone.main} 50%, ${tone.shadow} 100%)` }}
                          onClick={() => updateSetting('skinTone', id)}
                          title={id.replace('-', ' ')}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Hair Color */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium flex items-center gap-2">
                      <Palette className="h-3.5 w-3.5" />
                      Hair Color
                    </Label>
                    <div className="flex gap-1.5 flex-wrap">
                      {Object.entries(HAIR_COLORS).map(([id, color]) => (
                        <button
                          key={id}
                          className={`w-7 h-7 rounded-full border-2 transition-all hover:scale-110 ${settings.hairColor === id
                              ? 'border-blue-500 scale-110 shadow-lg'
                              : 'border-transparent hover:border-slate-300'
                            }`}
                          style={{ backgroundColor: color }}
                          onClick={() => updateSetting('hairColor', id)}
                          title={id.replace('-', ' ')}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Clothing Color */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium flex items-center gap-2">
                      <Shirt className="h-3.5 w-3.5" />
                      Clothing
                    </Label>
                    <div className="flex gap-1.5 flex-wrap">
                      {CLOTHING_PRESETS.map(preset => (
                        <button
                          key={preset.color}
                          className={`w-7 h-7 rounded-md border-2 transition-all hover:scale-110 ${settings.clothingColor === preset.color
                              ? 'border-blue-500 scale-110 shadow-lg'
                              : 'border-transparent hover:border-slate-300'
                            }`}
                          style={{ backgroundColor: preset.color }}
                          onClick={() => updateSetting('clothingColor', preset.color)}
                          title={preset.name}
                        />
                      ))}
                      <div className="relative">
                        <input
                          type="color"
                          value={settings.clothingColor}
                          onChange={(e) => updateSetting('clothingColor', e.target.value)}
                          className="absolute inset-0 w-7 h-7 opacity-0 cursor-pointer"
                        />
                        <div
                          className="w-7 h-7 rounded-md border-2 border-dashed border-slate-300 flex items-center justify-center"
                          style={{ backgroundColor: settings.clothingColor }}
                        >
                          <Palette className="h-3 w-3 text-white/70" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Caption Toggle */}
                  <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <Label className="text-xs">Show Caption</Label>
                    <Switch
                      checked={settings.showCaption}
                      onCheckedChange={(v) => updateSetting('showCaption', v)}
                    />
                  </div>
                </TabsContent>

                {/* Language Tab */}
                <TabsContent value="language" className="space-y-3 mt-3">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium">Sign Language</Label>
                    <Select
                      value={settings.language}
                      onValueChange={(v) => updateSetting('language', v)}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue>
                          <span className="flex items-center gap-2">
                            <span>{langConfig.flag}</span>
                            <span>{langConfig.name}</span>
                            <span className="text-xs text-slate-500">({langConfig.region})</span>
                          </span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {Object.entries(SIGN_LANGUAGE_GROUPS).map(([region, codes]) => (
                          <div key={region}>
                            <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-0">
                              {region}
                            </div>
                            {codes.map(code => {
                              const config = SIGN_LANGUAGE_CONFIG[code];
                              if (!config) return null;
                              return (
                                <SelectItem key={code} value={code}>
                                  <span className="flex items-center gap-2">
                                    <span>{config.flag}</span>
                                    <span className="font-medium">{config.name}</span>
                                    <span className="text-xs text-slate-500">- {config.fullName}</span>
                                  </span>
                                </SelectItem>
                              );
                            })}
                          </div>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Language Info */}
                  <div className="p-3 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{langConfig.flag}</span>
                      <div>
                        <p className="font-semibold text-sm">{langConfig.fullName}</p>
                        <p className="text-xs text-slate-500">{langConfig.region}</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-[10px]">
                      {langConfig.family} Family
                    </Badge>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-center">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <p className="text-lg font-bold text-blue-600">{Object.keys(SIGN_LANGUAGE_CONFIG).length}</p>
                      <p className="text-[10px] text-slate-600">Languages</p>
                    </div>
                    <div className="p-2 bg-emerald-50 rounded-lg">
                      <p className="text-lg font-bold text-emerald-600">8</p>
                      <p className="text-[10px] text-slate-600">African</p>
                    </div>
                    <div className="p-2 bg-purple-50 rounded-lg">
                      <p className="text-lg font-bold text-purple-600">5</p>
                      <p className="text-[10px] text-slate-600">Regions</p>
                    </div>
                  </div>

                  {/* Regional highlights with all languages */}
                  <div className="text-xs space-y-2 p-3 bg-slate-50 rounded-lg border">
                    <p className="font-semibold text-slate-700 flex items-center gap-1.5">
                      <span>🌍</span> All Supported Sign Languages:
                    </p>
                    <div className="space-y-1.5">
                      <div className="flex flex-wrap gap-1">
                        <span className="font-medium text-slate-600">Africa:</span>
                        <span className="text-slate-500">🇿🇦 SASL • 🇿🇼 ZSL • 🇰🇪 KSL • 🇺🇬 UGSL • 🇹🇿 TSL • 🇬🇭 GSL • 🇳🇬 NSL • 🇪🇹 ESL</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        <span className="font-medium text-slate-600">Americas:</span>
                        <span className="text-slate-500">🇺🇸 ASL • 🇲🇽 LSM • 🇧🇷 Libras • 🇦🇷 LSA</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        <span className="font-medium text-slate-600">Europe:</span>
                        <span className="text-slate-500">🇬🇧 BSL • 🇫🇷 LSF • 🇩🇪 DGS • 🇮🇹 LIS • 🇪🇸 LSE • 🇵🇱 PJM • 🇷🇺 RSL</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        <span className="font-medium text-slate-600">Asia-Pacific:</span>
                        <span className="text-slate-500">🇦🇺 Auslan • 🇳🇿 NZSL • 🇯🇵 JSL • 🇨🇳 CSL • 🇮🇳 ISL • 🇭🇰 HKSL</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        <span className="font-medium text-slate-600">International:</span>
                        <span className="text-slate-500">🌍 IS (International Sign)</span>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="absolute bottom-full right-0 mb-2 bg-red-500 text-white text-xs px-3 py-2 rounded-lg shadow-lg max-w-xs">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Avatar */}
        <SignLanguageAvatarLive
          text={currentText}
          language={settings.language}
          skinTone={settings.skinTone}
          hairColor={settings.hairColor}
          clothingColor={settings.clothingColor}
          size={isExpanded ? 'xlarge' : size}
          showCaption={settings.showCaption}
          isExpanded={isExpanded}
          isListening={isListening || isDemoMode}
          statusText={getStatusText()}
          statusColor={getStatusColor()}
          onClose={onClose}
          onExpand={() => setIsExpanded(!isExpanded)}
          onToggleListening={toggleMicListening}
          speechSupported={speechSupported}
        />

        {/* Settings button */}
        {showSettings && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute -top-1 -left-1 h-8 w-8 bg-slate-800/90 text-white hover:text-white hover:bg-slate-700 rounded-full shadow-lg border border-slate-600 z-[60]"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setShowSettingsPanel(prev => !prev);
            }}
            title="Interpreter Settings"
            data-testid="interpreter-settings-btn"
          >
            <Settings className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default LiveSignLanguagePanel;
